import React, { ReactElement, useCallback, useMemo, useContext, useState, useEffect } from "react";
import classNames from "classnames";
import { QualityHubTasksListItemProps, QualityHubTasksListItemState } from "./QualityHubTasksListItem.types";
import { SmallBody, SmallBodyBolder, CaptionBody, CaptionBolder } from "src/components/Text";
import { useSelector, useDispatch } from "react-redux";
import { SpintrTypes } from "src/typings";
import { createTaskStatus, updateTaskStatus } from "src/redux";
import { truncate, isSameDate } from "src/utils";
import { DecisionToggle } from "src/components/DecisionToggle";
import { Conditional } from "src/components/Conditional";
import { TaskStatusModel } from "src/api";
import { localize } from "src/l10n";
import { QualityHubTasksListContext } from "../QualityHubTasksListContext";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { CountdownCircle } from "src/components/CountdownCircle";
import { Toggle } from "@fluentui/react";
import { QualityHubVerificationDrawer } from "src/components/QualityHubVerificationDrawer";

// const statusChangeTimeout = 5 * 60 * 1000; // 5 minutes
const statusChangeTimeout = 24 * 60 * 60 * 1000; // debug

const navigationTimeoutSeconds = 5;

/**
 * QualityHubTasksListItem component
 *
 * @param {QualityHubTasksListItemProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTasksListItem({
    isExpanded = false,
    item,
    onItemClick,
    overdue = false,
    listMode = SpintrTypes.QualityHubTasksListMode.Execution,
    pendingChanges,
    onTaskChange,
    dragHandleProps,
    isDragging = false,
    isAnyItemDragging = false,
}: QualityHubTasksListItemProps): ReactElement | null {
    const [state, setState] = useState<QualityHubTasksListItemState>({
        intervalId: null,
        verificationDrawerOpen: false,
    });

    const { intervalId, secondsLeftUntilNavigation } = state;
    useEffect(() => () => clearTimeout(intervalId), [intervalId]);

    const dispatch = useDispatch();
    const {
        advanceFrom: advanceToNext,
        backwardFrom: goToPrevious,
        createAction: onCreateActionClick,
        setExpandedItemId,
    } = useContext(QualityHubTasksListContext);

    const status = useSelector<Spintr.AppState, Spintr.TaskListItemStatus>(
        (appState) => appState.qualityHub.listsById[item.listId]?.statuses?.find(
            (status) => status.itemId === item.id));

    const followUpCount = useSelector<Spintr.AppState, number>(
        (appState) => appState.qualityHub.allListIds
            .map((listId) => appState.qualityHub.listsById[listId])
            .flatMap((list) => list?.tasks ?? [])
            .filter((task) => task.followUpFromItemId === item.id)
            .filter((task) => typeof task.createdOn === "object")
            .filter((task) => isSameDate(task.createdOn, new Date()))
            .length);

    const smallViewMode = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.ui.isSmallViewMode);

    const onItemClicked = useCallback<React.MouseEventHandler>(
        (event) => {
            let el = event.target as (HTMLElement | SVGElement);
            while (el && el !== event.currentTarget) {
                const className = el instanceof HTMLElement 
                    ? el.className 
                    : el instanceof SVGElement 
                        ? el.className?.baseVal || ''
                        : '';

                if (
                    typeof className === "string" &&
                    (className.includes("DecisionToggle") || className.includes("ms-Toggle"))
                ) {
                    return;
                } else if (typeof className === "string" && className.includes("QualityHubTasksListItem")) {
                    break;
                }
                
                el = el.parentElement;
            }

            if (smallViewMode && !isExpanded) {
                setExpandedItemId(item.id);
                return;
            }

            onItemClick?.(item);
        },
        [onItemClick, item, isExpanded, setExpandedItemId, smallViewMode]);
    
    const startNavigationCountdown = useCallback(() => {
        advanceToNext(item.id);
        return;
        // disabled for now
        const intervalFn = () => setState((prevState) => {
            if (prevState.secondsLeftUntilNavigation > 1) {
                return {
                    ...prevState,
                    secondsLeftUntilNavigation: prevState.secondsLeftUntilNavigation - 1,
                };
            }

            clearInterval(prevState.intervalId);
            advanceToNext(item.id);

            return {
                ...prevState,
                secondsLeftUntilNavigation: undefined,
                intervalId: null,
            };
        });

        setState((prevState) => ({
            ...prevState,
            secondsLeftUntilNavigation: navigationTimeoutSeconds,
            intervalId: window.setInterval(intervalFn, 1000),
        }));
    }, [setState, item.id, advanceToNext]);

    const onCompletionToggled = useCallback((checked: boolean) => {
        const newStatus = checked
            ? SpintrTypes.TaskListItemStatus.Completed
            : SpintrTypes.TaskListItemStatus.Failed;

        if (status?.status === newStatus) {
            return;
        }

        if (status?.reportedOn) {
            const timeDiff = Date.now() - status.reportedOn.getTime();
            if (timeDiff > statusChangeTimeout) {
                return;
            }
        }

        if (item.requireVerification && newStatus === SpintrTypes.TaskListItemStatus.Completed) {
            setState((prevState) => ({
                ...prevState,
                verificationDrawerOpen: true,
                selectedStatus: newStatus,
            }));
            return;
        }

        dispatch(!status
            ? createTaskStatus({
                itemId: item.id,
                status: newStatus,
                reportedOn: new Date(),
            } as TaskStatusModel, item.listId)
            : updateTaskStatus(
                { itemId: item.id, status: newStatus },
                status,
                item.listId,
            ),
        );

        if (newStatus !== SpintrTypes.TaskListItemStatus.Failed) {
            startNavigationCountdown();
            return;
        }

        onCreateActionClick?.(item, (success: boolean) => {
            if (!success) {
                return;
            }

            startNavigationCountdown();
        });
    }, [dispatch, status, item, advanceToNext, startNavigationCountdown]);

    const completionStatus = status === undefined
        ? undefined
        : SpintrTypes.TaskListItemStatus.Completed === status.status;

    const decisionDisabled = useMemo(() => {
        if (!status?.reportedOn) {
            return false;
        }

        const timeDiff = Date.now() - status.reportedOn.getTime();

        return timeDiff > statusChangeTimeout;
    }, [status?.reportedOn]);

    const baseClassNames = classNames(
        "QualityHubTasksListItem", {
        "lightPrimaryBGColorHover": !smallViewMode,
        "QualityHubTasksListItem-expanded": isExpanded,
        "isDragging": isDragging,
        "isDraggingAny": isAnyItemDragging,
        "isClickable": onItemClick,
        });

    const descriptionEl = !item.description ? null : (
        <CaptionBody className="QualityHubTasksListItem-text-description"
                     color="contentNormal">
            {truncate(item.description, 100)}
        </CaptionBody>
    );

    const onCreateActionClicked = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        onCreateActionClick?.(item, (success: boolean) => {
            if (!success || !completionStatus) {
                return;
            }

            startNavigationCountdown();
        });
    }, [onCreateActionClick, item, completionStatus, startNavigationCountdown]);

    const cancelNavigation = useCallback<React.MouseEventHandler>((ev) => {
        ev.stopPropagation();
        ev.preventDefault();

        if (state.intervalId) {
            clearInterval(intervalId);
        }

        setState((prevState) => ({
            ...prevState,
            secondsLeftUntilNavigation: undefined,
            intervalId: null,
        }));
    }, [state.intervalId, setState]);

    const onDismissVerification = useCallback(() => setState((prevState) => ({
        ...prevState,
        verificationDrawerOpen: false,
        selectedStatus: undefined,
    })), [setState]);

    const onVerificationSubmitted = useCallback((files: File[]) => {
        const newStatus = state.selectedStatus;

        dispatch(!status
            ? createTaskStatus({
                itemId: item.id,
                status: newStatus,
                reportedOn: new Date(),
                files,
            } as TaskStatusModel, item.listId)
            : updateTaskStatus(
                { itemId: item.id, status: newStatus, files },
                status,
                item.listId,
            ),
        );

        setState((prevState) => ({
            ...prevState,
            verificationDrawerOpen: false,
            selectedStatus: undefined,
        }));

        if (newStatus !== SpintrTypes.TaskListItemStatus.Failed) {
            startNavigationCountdown();
            return;
        }

        onCreateActionClick?.(item, (success: boolean) => {
            if (!success) {
                return;
            }

            startNavigationCountdown();
        });
    }, [dispatch, status, item, startNavigationCountdown, state.selectedStatus]);

    const goToNext = useCallback<React.MouseEventHandler>(
        (ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            advanceToNext(item.id, true);
        },
        [secondsLeftUntilNavigation, advanceToNext, item.id]);

    const goBack = useCallback<React.MouseEventHandler>(
        (ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            goToPrevious(item.id);
        },
        [goToPrevious, item.id]);

    const onItemToggleChanged = useCallback((checked: boolean) => {
        const newStatus = checked
            ? SpintrTypes.ContentStatus.Published
            : SpintrTypes.ContentStatus.Deleted;

        onTaskChange?.(item, { contentStatus: newStatus });
    }, [item, onTaskChange]);

    // Determine the current content status (either pending changes or current status)
    const currentContentStatus = pendingChanges?.contentStatus !== undefined
        ? pendingChanges.contentStatus
        : item.contentStatus;

    const editMode = listMode === SpintrTypes.QualityHubTasksListMode.Customization
                  || listMode === SpintrTypes.QualityHubTasksListMode.Creation;

    const showDraftToggle = listMode === SpintrTypes.QualityHubTasksListMode.Customization && item.allowOverride;

    return (
        <>
            <div className={baseClassNames}
                     data-item-id={item.id}
                     onClick={onItemClicked}>
                <Conditional condition={editMode && dragHandleProps}>
                    <div className="QualityHubTasksListItem-dragHandle"
                         {...dragHandleProps}>
                        <Visage2Icon icon="menu-1"
                                     size="medium"
                                     color="spintrGrey" />
                    </div>
                </Conditional>
                <div className="QualityHubTasksListItem-textWrapper">
                    <Conditional condition={item.itemType === SpintrTypes.QualityHubItemType.Reminder}>
                        <Visage2Icon className="QualityHubTasksListItem-reminderIcon"
                            icon="reminder-1"
                            type="custom" />
                    </Conditional>
                    <div className="QualityHubTasksListItem-text">
                        <div className="QualityHubTasksListItem-text-title">
                            <SmallBody as="span"
                                    className="QualityHubTasksListItem-itemTitle"
                                    color="contentDark"
                                    weight={isExpanded && smallViewMode ? "medium" : undefined}>
                                {item.title}
                            </SmallBody>
                            <Conditional condition={overdue && item.itemType !== SpintrTypes.QualityHubItemType.Reminder}>
                                <CaptionBody as="span" color="pink" className="overdue-pill">
                                    {localize("QH_OVERDUE")}
                                </CaptionBody>
                            </Conditional>
                            <Conditional
                                condition={
                                    editMode &&
                                    item.originalData && (
                                        item.originalData.title !== item.title ||
                                        item.originalData.description !== item.description ||
                                        item.originalData.groupId !== item.groupId)
                                }
                            >
                                <CaptionBody as="span" color="contentDark" className="customized-pill">
                                    {localize("CUSTOMIZED")}
                                </CaptionBody>
                            </Conditional>
                            <Conditional condition={followUpCount > 0}>
                                <div title={localize("FOLLOW_UP_CREATED")}>
                                    <Visage2Icon className="QualityHubTasksListItem-followUpIcon"
                                                 icon="warning-2"
                                                 color="yellow"
                                                 type="outline" />
                                </div>
                            </Conditional>
                            <Conditional condition={!isExpanded && smallViewMode && status && (
                                status.status === SpintrTypes.TaskListItemStatus.Completed ||
                                status.status === SpintrTypes.TaskListItemStatus.Failed
                            )}>
                                <div className="QualityHubTasksListItem-completedIcon">
                                    <Visage2Icon color="green"
                                                 icon="tick-circle"
                                                 size="medium"
                                                 type="bold" />
                                </div>
                            </Conditional>
                        </div>
                        <Conditional condition={!smallViewMode}>
                            {descriptionEl}
                        </Conditional>
                    </div>
                </div>
                <Conditional condition={!editMode}>
                    <div className="QualityHubTasksListItem-toggle">
                        <Conditional condition={item.guidingQuestion}>
                            <CaptionBolder className="QualityHubTasksListItem-question">
                                {item.guidingQuestion}
                            </CaptionBolder>
                        </Conditional>
                        <div className="QualityHubTasksListItem-slider">
                            <Conditional condition={item.itemType !== SpintrTypes.QualityHubItemType.Reminder
                                                 && item.contentStatus === SpintrTypes.ContentStatus.Published}>
                                <DecisionToggle clickable={!smallViewMode}
                                                disabled={decisionDisabled}
                                                fullWidth={smallViewMode && isExpanded}
                                                onChange={onCompletionToggled}
                                                value={completionStatus} />
                            </Conditional>
                        </div>
                    </div>
                </Conditional>
                <Conditional condition={showDraftToggle}>
                    <Toggle
                        checked={currentContentStatus === SpintrTypes.ContentStatus.Published}
                        onChange={(_, checked) => onItemToggleChanged(checked ?? false)}
                    />
                </Conditional>
                <Conditional condition={smallViewMode && isExpanded}>
                    {descriptionEl}
                    <div className="QualityHubTasksListItem-actionRow">
                        {/*
                        <div aria-role="button"
                             onClick={goBack}
                             role="button"> 
                            <Visage2Icon color="mid-grey"
                                         icon="arrow-left-3"
                                         size="big" />
                        </div>
                        */}
                        <Conditional condition={item.itemType !== SpintrTypes.QualityHubItemType.Reminder}>
                            <div aria-role="button"
                                 className="QualityHubTasksListItem-createActionButton"
                                 role="button"
                                 onClick={onCreateActionClicked}>
                                <SmallBodyBolder color="blue">
                                    {localize("QH_CREATE_ACTION")}
                                </SmallBodyBolder>
                            </div>
                        </Conditional>
                        <Conditional condition={isExpanded && secondsLeftUntilNavigation}>
                            <div aria-role="button"
                                 className="QualityHubTasksListItem-cancelNavigationCircle"
                                 onClick={cancelNavigation}
                                 role="button">
                                <CountdownCircle maxValue={5}
                                                 thickness={10}
                                                 value={secondsLeftUntilNavigation || 0} />
                                <SmallBody className="QualityHubTasksListItem-cancelNavigationText"
                                           color="pink">
                                    {localize("Avbryt")}
                                </SmallBody>
                            </div>
                        </Conditional>
                        {/*
                        <Conditional condition={!secondsLeftUntilNavigation}>
                            <div aria-role="button"
                                 onClick={goToNext}
                                 role="button">
                                <Visage2Icon color="mid-grey"
                                             icon="arrow-right-2"
                                             size="big" />
                            </div>
                        </Conditional>
                        <Conditional condition={secondsLeftUntilNavigation}>
                            <div aria-role="button"
                                 className="QualityHubTasksListItem-cancelNavigationButton"
                                 onClick={cancelNavigation}
                                 role="button">
                                <Visage2Icon color="mid-grey"
                                             icon="close-circle"
                                             size="big" />
                                {secondsLeftUntilNavigation}
                            </div>
                        </Conditional>
                        */}
                    </div>
                </Conditional>
            </div>
            <QualityHubVerificationDrawer onDismiss={onDismissVerification}
                                          onVerificationSubmitted={onVerificationSubmitted}
                                          open={state.verificationDrawerOpen}
                                          verificationTypes={item.verificationType} />
        </>
    );
}

export default QualityHubTasksListItem;

